import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { JourneyDots, Avatar } from '../components/ui';
import { C } from '../theme';
import { obsAPI } from '../api';
import { canAdminister, canObserve, canDebrief } from '../lib/permissions';
import TabBar from '../components/TabBar';
import AdminOverview from './admin/AdminOverview';
import { adminAPI } from '../api';


export default function HomePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    observationCount: 0,
    schoolCount: 0,
  });
  const [history, setHistory] = useState([]);
  const hour     = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning,' : hour < 17 ? 'Good afternoon,' : 'Good evening,';
  const [pending, setPending] = useState([]);
  const [overview, setOverview] = useState(null);
  
  useEffect(() => {
    fetchDashboardStats();
    // Observations completed but not yet debriefed.
    // An administrator's landing screen is the overview, not their own activity.
    if (canAdminister(user)) {
      adminAPI.overview()
        .then(({ data }) => setOverview(data))
        .catch(err => console.error('Could not load the overview:', err));
    }
    if (canDebrief(user)) obsAPI.list({ status: 'obs_complete,debrief_pending' })
      .then(({ data }) => setPending(data.results ?? data))
      .catch(err => console.error('Could not load pending debriefs:', err));
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const response = await obsAPI.list();
      const data = response.data;
      const uniqueSchools = new Set(
        data.results.map(item => item.school_id)
      );
      setStats({
        observationCount: data.count || 0, schoolCount: uniqueSchools.size || 0,
      });
      // Recent activity
      const formattedHistory = data.results.sort((a, b) => new Date(b.started_at) - new Date(a.started_at)) // latest first
        .slice(0, 5)
        .map((item, index) => {
        const initials = item.subject?.slice(0, 2)?.toUpperCase() || 'OB';
        const avatarStyles = [
          {
            avBg:'#EAF6FC',
            avColor:'#1A4A6B'
          },
          {
            avBg:'var(--amber-l)',
            avColor:'var(--amber-d)'
          },
          {
            avBg:'var(--purple-l)',
            avColor:'var(--purple-d)'
          },
        ];
        const style =
          avatarStyles[index % avatarStyles.length];
        return {
          id: item.id,
          name: `You Observed ${item.teacher_name}`,
          sub: `${item.school_name} · ${item.grade_level} · ${item.subject}`,
          initials,
          avBg: style.avBg,
          avColor: style.avColor,
          when: formatDate(item.started_at),
          status: item.status,
        };
      });
    setHistory(formattedHistory);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    }
  };

  const formatDate = (dateString) => {
  const date = new Date(dateString);
  const today = new Date();
  const isToday = date.toDateString() === today.toDateString();
  if (isToday) {
      return 'Today';
    }
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
    });
  };

  return (
    <div className="screen">
      {/* Top bar */}
      <div className="topbar">
        <div className="topbar-row">
          <div className="wordmark">
            <div className="wm-icon">
              <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="6" stroke="#fff" strokeWidth="1.8"/>
                <path d="M10 7v4l2.5 1.5" stroke="#fff" strokeWidth="1.6" strokeLinecap="round"/>
              </svg>
            </div>
            <div>
              <div className="wm-name">ELO Insights</div>
              <div className="wm-suite">Whitehead Ross</div>
            </div>
          </div>
          <button className="avatar-btn" onClick={logout} title="Sign out">
            {user?.initials || 'EW'}
          </button>
        </div>
        <div className="greeting">
          <div className="greeting-sub">{greeting}</div>
          <div className="greeting-name">{user?.first_name || 'Emma'}</div>
          <span className="greeting-role">
            {[user?.role_display, user?.school_name || user?.district_name]
              .filter(Boolean).join(' · ')}
          </span>
        </div>
      </div>

      <div className="scroll">
        <div className="pad">
          {/* Start observation CTA — hidden for accounts that cannot observe,
              rather than shown and then refused. */}
          {canObserve(user) && (
          <div className="start-obs-card" onClick={() => navigate('/observe/context')} role="button">
            <div style={{ width:40, height:40, borderRadius:10, background:'var(--blue-d)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <circle cx="9" cy="9" r="5.5" stroke="#fff" strokeWidth="1.6"/>
                <circle cx="9" cy="9" r="2.5" fill="#fff"/>
              </svg>
            </div>
            <div style={{ flex:1 }}>
              <div style={{ color:'#fff', fontSize:15, fontWeight:600, marginBottom:2 }}>Start observation</div>
              <div style={{ color:'rgba(255,255,255,0.5)', fontSize:12 }}>10–15 minutes · guided evidence capture</div>
            </div>
            <div style={{ color:'var(--blue-m)', fontSize:24, fontWeight:300 }}>+</div>
          </div>)}

          {canAdminister(user) && overview && <AdminOverview data={overview} />}

          {/* Administration */}
          {canAdminister(user) && (
            <div
              className="admin-card"
              role="button"
              tabIndex={0}
              onClick={() => navigate('/admin')}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') navigate('/admin'); }}
            >
              <div className="admin-card-icon">
                <svg width="17" height="17" viewBox="0 0 18 18" fill="none">
                  <circle cx="9" cy="6" r="3" stroke="#fff" strokeWidth="1.5"/>
                  <path d="M3.5 15c0-2.8 2.5-4.5 5.5-4.5s5.5 1.7 5.5 4.5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
              <div style={{ flex:1 }}>
                <div className="admin-card-title">Administration</div>
                <div className="admin-card-sub">Users, roles, schools and patches</div>
              </div>
              <div style={{ color:'rgba(255,255,255,0.5)', fontSize:16 }}>›</div>
            </div>
          )}

          {/* Pending debriefs — tap through to the full list. */}
          {pending.length > 0 && canDebrief(user) && (
            <div
              className="pending-card"
              role="button"
              tabIndex={0}
              onClick={() => navigate('/debriefs')}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') navigate('/debriefs'); }}
            >
              <div style={{ width:36, height:36, borderRadius:9, background:'var(--amber)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M3 5h10M3 8h7M3 11h5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13, fontWeight:500, color:'var(--amber-d)' }}>
                  {pending.length === 1
                    ? 'Debrief pending'
                    : `${pending.length} debriefs pending`}
                </div>
                <div style={{ fontSize:11, color:'var(--amber-d)', opacity:0.8 }}>
                  {pending.length === 1
                    ? `${pending[0].teacher_display_name || pending[0].teacher_name} · ${pending[0].grade_level} · ${pending[0].subject} · tap to start`
                    : 'Tap to see who is waiting'}
                </div>
              </div>
              <div style={{ color:'var(--amber)', fontSize:16 }}>›</div>
            </div>)}

          {/* Dashboard link */}
          {canObserve(user) && (user?.role_code === 'school_leader') && (
          <div
            style={{ background:'var(--white)', borderRadius:'var(--r)', border:'0.5px solid var(--border)', padding:14, marginBottom:14, cursor:'pointer', display:'flex', alignItems:'center', gap:10 }}
            onClick={() => navigate('/dashboard')}
          >
            <div style={{ width:32, height:32, borderRadius:8, background:'var(--blue-l)', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect x="2" y="8" width="2.5" height="6" rx="0.5" fill="var(--blue)"/>
                <rect x="6.5" y="5" width="2.5" height="9" rx="0.5" fill="var(--blue)"/>
                <rect x="11" y="2" width="2.5" height="12" rx="0.5" fill="var(--blue)"/>
              </svg>
            </div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:13, fontWeight:500, color:'var(--text)' }}>Dashboard</div>
              <div style={{ fontSize:11, color:'var(--text2)' }}>Learning trends &amp; patterns</div>
            </div>
            <div style={{ color:'var(--text3)', fontSize:16 }}>›</div>
          </div>
          )}

          {/* Stats */}
          <div className="stat-grid">
            <div className="stat">
              <div className="stat-n">{stats.observationCount}</div>
              <div className="stat-l">Observations conducted </div>
            </div>

            <div className="stat">
              <div className="stat-n">{stats.schoolCount}</div>
              <div className="stat-l">Schools visited</div>
            </div>
          </div>


          {/* Recent activity — an account that does not observe has none of
              its own, and the administration screens carry the full data. */}
          {canObserve(user) && (<>
          <div className="sec-lbl">Recent activity</div>
          {history.map((item, i) => (
            <div
              key={item.id}
              className="hist-item animate-fade-up"
              style={{ animationDelay: `${i * 60}ms` }}
              onClick={() => navigate('/history')}
            >
              <div className="hist-row" style={{ marginBottom:0 }}>
                <div className="hist-av" style={{ background:item.avBg, color:item.avColor }}>{item.initials}</div>
                <div style={{ flex:1 }}>
                  <div className="hist-name">{item.name}</div>
                  <div style={{ fontSize:11, color:'var(--text2)' }}>{item.sub}</div>
                </div>
                <div className="hist-time">{item.when}</div>
              </div>
              <JourneyDots status={item.status} />
            </div>
          ))}
          </>)}
        </div>
      </div>

      <TabBar current="home" />
    </div>
  );
}
